import { motion } from "framer-motion";
import React, { type FC, type ReactNode } from "react";

interface AlertProps {
  variant: "info" | "warning" | "error" | "success";
  children: ReactNode;
  className?: string;
  title?: string;
  action?: ReactNode;
  size?: "sm" | "md";
  glyph?: string;
  animate?: boolean;
}

export const Alert: FC<AlertProps> = ({
  variant,
  children,
  className = "",
  title,
  action,
  size = "md",
  glyph,
  animate = false,
}) => {
  const styles = {
    error:
      "border-rose-200 bg-rose-50/60 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-100",
    info: "border-slate-300 bg-slate-100/80 text-slate-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-slate-100",
    success:
      "border-green-200 bg-green-50/60 text-green-700 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-100",
    warning:
      "border-amber-300/70 bg-amber-50/60 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100",
  };

  const sizeStyles = {
    md: "rounded-none px-3 py-2 text-xs",
    sm: "rounded-none px-2.5 py-1.5 text-xs",
  };

  const defaultGlyphs = { error: "✕", info: "■", success: "✓", warning: "!" };

  const displayGlyph = glyph ?? defaultGlyphs[variant];

  const content = (
    <div className="flex items-start gap-2">
      {displayGlyph && (
        <span className="font-mono text-sm leading-none">{displayGlyph}</span>
      )}
      <div className="flex-1">
        {action ? (
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              {title && (
                <div className="mb-1 font-mono text-[10px] font-bold uppercase tracking-widest">
                  {title}
                </div>
              )}
              {children}
            </div>
            {action}
          </div>
        ) : (
          <>
            {title && (
              <div className="mb-1 font-mono text-[10px] font-bold uppercase tracking-widest">
                {title}
              </div>
            )}
            {children}
          </>
        )}
      </div>
    </div>
  );

  const baseClasses = `border ${styles[variant]} ${sizeStyles[size]} ${className}`;

  if (animate) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className={baseClasses}
      >
        {content}
      </motion.div>
    );
  }

  return <div className={baseClasses}>{content}</div>;
};
